import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import PageSkeleton from "./PageSkeleton";

function waitForImagesInside(container, timeout = 5000) {
  return new Promise((resolve) => {
    if (!container) {
      resolve();
      return;
    }

    const images = Array.from(container.querySelectorAll("img")).filter(
      (img) => img.loading !== "lazy"
    );

    if (images.length === 0) {
      resolve();
      return;
    }

    let finished = false;
    let loadedCount = 0;

    const finish = () => {
      if (finished) return;

      finished = true;
      clearTimeout(timer);

      images.forEach((img) => {
        img.removeEventListener("load", handleDone);
        img.removeEventListener("error", handleDone);
      });

      resolve();
    };

    const handleDone = () => {
      loadedCount += 1;

      if (loadedCount >= images.length) {
        finish();
      }
    };

    const timer = setTimeout(finish, timeout);

    images.forEach((img) => {
      if (img.complete && img.naturalWidth > 0) {
        handleDone();
      } else {
        img.addEventListener("load", handleDone);
        img.addEventListener("error", handleDone);
      }
    });
  });
}

export default function GlobalImageLoader() {
  const location = useLocation();
  const contentRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setLoaded(false);
    setShowSkeleton(false);

    const skeletonTimer = setTimeout(() => {
      if (!cancelled) {
        setShowSkeleton(true);
      }
    }, 250);

    requestAnimationFrame(async () => {
      await waitForImagesInside(contentRef.current, 5000);

      if (cancelled) return;

      clearTimeout(skeletonTimer);
      setLoaded(true);
      setShowSkeleton(false);
    });

    return () => {
      cancelled = true;
      clearTimeout(skeletonTimer);
    };
  }, [location.pathname]);

  return (
    <div className="relative min-h-screen">
      {showSkeleton && !loaded && (
        <div className="fixed inset-0 z-[9999] bg-white">
          <PageSkeleton />
        </div>
      )}

      <div
        ref={contentRef}
        className={
          loaded
            ? "opacity-100 transition-opacity duration-300"
            : "pointer-events-none opacity-0"
        }
      >
        <Outlet />
      </div>
    </div>
  );
}